import classNames from 'classnames'
import { getIn, useFormikContext } from 'formik'
import { ChangeEvent, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'

import {
  BaseFieldProps,
  InputSize,
  LabelConstraint,
} from '@ui/components/Input/BaseField/BaseField'
import { Input } from '@ui/components/Input/Input'
import { ListItem } from '@ui/components/List'
import { CaretIcon } from '@ui/icons/Caret.icon'
import { IconFCType } from '@ui/icons/types'

import { useWindowResize } from '@utils/domUtils'

import { NameDto } from '@model/NameDto'

import { makeResolveFlagIcon } from '@global/locale/localeIcons'

import { Dropdown, DropdownCard, DropdownList } from '@shared/components/Dropdown'
import { InputSearch } from '@shared/components/InputSearch'

import styles from './DropdownInput.module.scss'

export type DropdownOption<TId extends string | number = string | number> = NameDto<TId> & {
  startIcon?: IconFCType
  description?: string
  selectionVariant?: 'checkbox' | 'radio'
  trailText?: string
  infoText?: string
  badgeText?: string
}

interface CommonDropdownInputProps<T extends DropdownOption> {
  value?: T['id']
  onChange?: (option: T) => void
  options?: T[]
  hasCheckMark?: boolean
  selectionVariant?: 'checkbox' | 'radio'
  hint?: string | React.ReactNode
  error?: boolean | string
  rightText?: BaseFieldProps['rightText']
  isValid?: boolean
  isFullWidth?: boolean
  isWidthFixed?: boolean
  disabled?: boolean
  startIcon?: IconFCType
  onClick?: () => void
  validate?: (value: any) => string | undefined
  name?: string
  required?: boolean
  readOnly?: boolean
  maxLength?: number
  displayValue?: 'id' | 'name'
  dropdownListVariant?: 'compact' | 'default'
  size?: InputSize
  searchInput?: {
    value: string
    handleChange?: (event: ChangeEvent<HTMLInputElement>) => void
    handleClear?: () => void
    className?: string
  }
  showLabel?: boolean
  wrapText?: boolean
}

export type DropdownInputProps<T extends DropdownOption<string | number>> =
  CommonDropdownInputProps<T> & LabelConstraint

export const DropdownInput = <T extends DropdownOption<string | number>>(
  props: DropdownInputProps<T>
): JSX.Element => {
  const { t } = useTranslation()
  const {
    value,
    onChange,
    options,
    label,
    placeholder,
    hint,
    error,
    startIcon,

    searchInput,
    isFullWidth = false,
    disabled = false,
    isWidthFixed = true,
    onClick,
    validate,
    name,
    required,
    readOnly,
    maxLength,
    displayValue = 'name',
    hasCheckMark,
    dropdownListVariant = 'default',
    size = 'xl',
    showLabel,
    rightText,
    // wrapText,
  } = props

  const internalLabel = label ?? placeholder

  const internalPlaceholder = placeholder ?? label

  const inputLabelProps = internalLabel
    ? { label: internalLabel, placeholder: internalPlaceholder }
    : {}

  const isMobile = useWindowResize()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isDropdownOpen) {
      inputRef.current?.blur()
    }
  }, [isDropdownOpen])

  const selectedName = useMemo(() => {
    if (!options || options.length === 0) {
      return value
    }
    const option = options.find((option) => option.id === value)
    return displayValue === 'id' ? option?.id : option?.name
  }, [value, options, displayValue])

  const formikContext = useFormikContext()
  const formikError = name ? getIn(formikContext.errors, name) : undefined
  const formikTouched = name ? getIn(formikContext.touched, name) : false

  const validationError = validate ? validate(value) : undefined
  const fieldError =
    formikError || validationError || (typeof error === 'string' ? error : undefined)
  const showHint = formikTouched && fieldError ? fieldError : hint

  const handleSelect = (option: T) => {
    if (name) {
      formikContext.setFieldValue(name, option.id)
      formikContext.setFieldTouched(name, true)
    }
    onChange?.(option)
    setIsDropdownOpen(false)
  }

  const handleWrapperClick = () => {
    if (disabled || readOnly) return

    if (onClick) {
      onClick()
    } else {
      setIsDropdownOpen(true)
    }
  }

  const handleEndIconClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (disabled || readOnly) return

    if (onClick) {
      onClick()
      return
    }

    setIsDropdownOpen((prev) => !prev)

    if (!isDropdownOpen) {
      inputRef.current?.focus()
    } else {
      inputRef.current?.blur()
    }
  }

  const handleBlur = () => {
    if (name) {
      formikContext.setFieldTouched(name, true)
    }
  }

  const renderOptions = (opts: typeof options) =>
    opts?.map((option) => {
      const isActive = option.id === value

      const selectionVariant =
        option.selectionVariant ?? props.selectionVariant ?? (hasCheckMark ? 'checkbox' : undefined)

      const StartIcon = option.startIcon ?? makeResolveFlagIcon(value?.toString())

      return (
        <ListItem
          key={option.id}
          onClick={() => handleSelect(option)}
          active={isActive}
          startSlot={{ icon: StartIcon }}
          endSlot={
            selectionVariant === 'radio'
              ? { radio: true }
              : selectionVariant === 'checkbox'
              ? { checkbox: true }
              : undefined
          }
          description={{ title: option.description, subtitle: option.infoText }}
          // trailText={option.trailText}
          // d={option.infoText}
          // badgeText={option.badgeText}
          // wrapText={wrapText}
        >
          {option.name}
        </ListItem>
      )
    })

  return (
    <div
      className={classNames({
        [styles.fullWidth]: isFullWidth,
      })}
    >
      <Dropdown
        direction='top'
        isOpen={isDropdownOpen}
        setIsOpen={setIsDropdownOpen}
        label={label ?? placeholder}
        disabled={!!onClick || disabled}
        isWidthFixed={isWidthFixed}
        modalCanSearch={!!searchInput}
        modalRender={
          searchInput && isMobile
            ? (ctx) => (
                <DropdownCard>
                  <DropdownList variant={dropdownListVariant}>
                    {!ctx.search
                      ? renderOptions(options)
                      : renderOptions(
                          options?.filter((option) =>
                            option.name.toLowerCase().includes(ctx.search.toLowerCase())
                          )
                        )}
                  </DropdownList>
                </DropdownCard>
              )
            : undefined
        }
        content={
          <DropdownCard>
            {searchInput && !isMobile && (
              <InputSearch
                label={t('Search')}
                className={classNames(styles.searchField, searchInput?.className)}
                value={searchInput?.value}
                onChange={searchInput?.handleChange}
                onClear={searchInput?.handleClear}
                isAutoFocus
              />
            )}
            <DropdownList variant={dropdownListVariant} onMouseOver={(e) => e.stopPropagation()}>
              {!searchInput?.value
                ? renderOptions(options)
                : renderOptions(
                    options?.filter((option) =>
                      option.name.toLowerCase().includes(searchInput.value.toLowerCase())
                    )
                  )}
            </DropdownList>
          </DropdownCard>
        }
      >
        <Input
          {...inputLabelProps}
          value={selectedName || ''}
          onWrapperClick={handleWrapperClick}
          onBlur={handleBlur}
          ref={inputRef}
          rightText={rightText}
          onEndIconClick={handleEndIconClick}
          rightIcon={CaretIcon.Down}
          hint={showHint}
          error={formikTouched && !!fieldError}
          readOnly={true}
          leftIcon={startIcon}
          disabled={disabled}
          name={name}
          required={required}
          maxLength={maxLength}
          size={size}
          showLabel={showLabel}
        />
      </Dropdown>
    </div>
  )
}
